import React from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import CustomTextField from "./common/CustomTextField";
import CustomSelect from "./common/CustomSelect";
import { RateSheetFileName } from "../services/RateTemplateService";

interface RateSheetFileNameComponentProps {
  textFieldValue: string;
  selectValue: string;
  onTextFieldChange: (value: string) => void;
  onSelectChange: (value: string) => void;
}

const RateSheetFileNameComponent: React.FC<RateSheetFileNameComponentProps> = ({
  textFieldValue,
  selectValue,
  onTextFieldChange,
  onSelectChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const transformText = (input: string): string => {
    return input
      .replace(/\[([^\]]*)\]/g, (bracket) => {
        let inside = bracket.slice(1, -1).trim();
        if (!inside) return "[]";
        let items = inside
          .split(",")
          .map((item) => item.trim().replace(/^['"]|['"]$/g, ""))
          .filter((item) => item !== "");
        return items.map((item) => `[${item}]`).join("");
      })
      .replace(/\s{2,}/g, " ");
  };

  const handleSelectChange = (value: string | number | (string | number)[]) => {
    onSelectChange?.(value.toString());

    let prefix = textFieldValue;
    let bracket = "[]";

    const match = prefix.match(/^(.*?)(\s*(\[.*\]))?$/);
    if (match) {
      prefix = match[1] || "";
      if (match[3]) {
        bracket = match[3];
      }
    }

    let inside = bracket.slice(1, -1).trim();

    let items = inside ? inside.split(",").map((x) => x.trim()) : [];
    items.push(value.toString().replace(/^['"]|['"]$/g, ""));
    items = items.filter((item) => item !== "");

    let newBracket = `[${items.join(", ")}]`;

    let combined = newBracket === "[]" ? prefix : prefix + " " + newBracket;
    onTextFieldChange(transformText(combined));
  };
  const handleTextFieldChange = (value: string) => {
    onTextFieldChange(transformText(value));
  };

  return (
    <Box
      display="flex"
      flexDirection={isMobile ? "column" : "row"}
      alignItems={isMobile ? "stretch" : "center"}
      sx={{
        pt: 0,
        pr: { xs: 2, sm: 3, md: 4 },
        pb: { xs: 2, sm: 3, md: 4 },
        pl: { xs: 2, sm: 3, md: 4 },
        minWidth: "250px",
        gap: isMobile ? 2 : 0,
      }}
    >
      <Box flex="1">
        <CustomTextField
          label="Rate Sheet File Name"
          value={textFieldValue}
          fullWidth
          placeholder="rate sheet file name"
          onChange={(event) => handleTextFieldChange(event.target.value)}
          sx={{
            minWidth: "250px",
            marginBottom: isMobile ? 2 : 0,
            "& .MuiInputBase-root": {
              borderRadius: isMobile ? "8px" : "8px 0 0 8px",
            },
          }}
        />
      </Box>

      <Box marginTop={isMobile ? 0 : "20px"} width={isMobile ? "100%" : undefined}>
        <CustomSelect
          value={selectValue}
          options={Object.entries(RateSheetFileName)
            .filter(([key, value]) => typeof value === "number")
            .map(([key]) => ({
              label: key,
              value: key,
            }))}
          onChange={(value) => handleSelectChange(value)}
          sx={{
            width: isMobile ? "100%" : "190px",
              borderRadius: isMobile ? "8px" : "0 8px 8px 0",
            "& .MuiOutlinedInput-root": {
              fontSize: "12px",
              height: "40px",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderLeft: isMobile ? undefined : "none",
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default RateSheetFileNameComponent;
